import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiRefreshCw } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";
import { formatMoneyCents } from "../../ui/Money";
import { ApiAuth, ApiError } from "../../../lib/api";
import { useBillingItems, usePaymentConfig } from "./hooks";
import { HeroSummary } from "./HeroSummary";
import { Filters, FiltersValue, rangeToTimestamps } from "./Filters";
import { ItemsTable } from "./ItemsTable";
import { PayDrawer } from "./PayDrawer";
import { BillingItem } from "./types";
import { DocumentsList } from "./DocumentsList";

type Props = {
  auth: ApiAuth;
  locationId: string | null;
};

const RefreshButton = ({
  isLoading,
  onRefresh,
}: {
  isLoading: boolean;
  onRefresh: () => void;
}) => (
  <IconButton
    aria-label="Refresh"
    icon={<FiRefreshCw />}
    onClick={onRefresh}
    isLoading={isLoading}
    size="sm"
    variant="outline"
    borderColor="#e6e6e6"
    color="#1a1f36"
    borderRadius="8px"
    mb={3}
  />
);

export const BillingView = ({ auth, locationId }: Props) => {
  const config = usePaymentConfig(auth, locationId);
  const stripePublicKey = config.data?.stripePublicKey || null;
  const configErrorMsg = config.isError
    ? (config.error as ApiError)?.message ||
      "Payments are not configured for this clinic."
    : null;

  const [filters, setFilters] = useState<FiltersValue>({
    type: "",
    range: "all",
    status: "outstanding",
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { from, to } = useMemo(
    () => rangeToTimestamps(filters.range),
    [filters.range]
  );

  const query = useBillingItems(auth, {
    status: filters.status,
    type: filters.type || null,
    from,
    to,
  });

  const items: BillingItem[] = query.data?.items ?? [];
  // This screen only ever shows the signed-in patient's own account, so paying
  // is always on offer. A guardian paying for a dependent does it from the
  // dependent's own Account screen, where the policy decides.
  const canPay = true;

  // Drop selections when the underlying items disappear (e.g. after a payment).
  useEffect(() => {
    if (!query.data) return;
    const knownIds = new Set(items.map((i) => i.itemId));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (knownIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [query.data, items]);

  // Clear selection when switching status filter — selecting "paid" items
  // and then flipping to "outstanding" would otherwise carry stale ids.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters.status]);

  const toggle = (id: string) => {
    if (!canPay) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (selectAll: boolean) => {
    if (!canPay) return;
    if (!selectAll) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(
      new Set(
        items
          .filter((i) => i.amounts.payableNowCents > 0)
          .map((i) => i.itemId)
      )
    );
  };

  const onPayAll = () => {
    if (!canPay) return;
    toggleAll(true);
    onOpen();
  };

  const selectedTotal = useMemo(() => {
    let sum = 0;
    for (const item of items) {
      if (selectedIds.has(item.itemId)) sum += item.amounts.payableNowCents;
    }
    return sum;
  }, [items, selectedIds]);

  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.itemId)),
    [items, selectedIds]
  );

  const renderRowExtras = (item: BillingItem) => (
    <DocumentsList auth={auth} itemId={item.itemId} enabled={true} />
  );

  return (
    <Box>
      <HeroSummary
        data={query.data}
        isLoading={query.isLoading}
        isError={query.isError}
        onRetry={() => query.refetch()}
        onPayAll={onPayAll}
        readOnly={!canPay}
      />
      {query.data && query.data.unreadableItemCount > 0 && (
        <Box
          mb={3}
          px={4}
          py={3}
          bg="#fef0c7"
          borderRadius="8px"
          fontSize="13px"
          color="#b54708"
        >
          {query.data.unreadableItemCount === 1
            ? "One item on your account could not be shown here."
            : `${query.data.unreadableItemCount} items on your account could not be shown here.`}{" "}
          Refresh the page, or ask the clinic if it stays.
        </Box>
      )}
      {canPay && configErrorMsg && (
        <Box
          mb={3}
          px={4}
          py={3}
          bg="#fef0c7"
          borderRadius="8px"
          fontSize="13px"
          color="#b54708"
        >
          {configErrorMsg}
        </Box>
      )}
      <Flex justify="space-between" align="flex-end" gap={3} flexWrap="wrap">
        <Filters value={filters} onChange={setFilters} />
        <RefreshButton
          isLoading={query.isFetching}
          onRefresh={() => query.refetch()}
        />
      </Flex>
      <ItemsTable
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        selectedIds={selectedIds}
        onToggle={toggle}
        onToggleAll={toggleAll}
        renderRowExtras={renderRowExtras}
      />

      {canPay && selectedIds.size > 0 && (
        <Flex
          position="sticky"
          bottom={2}
          mt={4}
          align="center"
          justify="space-between"
          bg="white"
          borderWidth="1px"
          borderColor="#e6e6e6"
          borderRadius="12px"
          boxShadow="0 4px 14px rgba(15,23,42,0.08)"
          px={4}
          py={3}
        >
          <HStack spacing={3}>
            <Text fontSize="14px" color="#1a1f36" fontWeight="600">
              {selectedIds.size} {selectedIds.size === 1 ? "item" : "items"} selected
            </Text>
            <Text fontSize="14px" color="#6b7280">
              · {formatMoneyCents(selectedTotal)}
            </Text>
          </HStack>
          <Button
            bg="#1a1f36"
            color="white"
            _hover={{ bg: "#2d3348" }}
            onClick={onOpen}
            size="md"
            borderRadius="8px"
            fontSize="14px"
            fontWeight="500"
            px={5}
          >
            Pay {formatMoneyCents(selectedTotal)}
          </Button>
        </Flex>
      )}

      <PayDrawer
        isOpen={isOpen}
        onClose={onClose}
        items={selectedItems}
        selectedIds={selectedIds}
        auth={auth}
        stripePublicKey={stripePublicKey}
        stripeAccount={config.data?.stripeAccount || null}
        locationId={locationId}
      />
    </Box>
  );
};
